// Display formatters for the back office. Pure, no Intl (same rules as the shared core).
// Money arrives as integer centavos from the server and is display-only.

const MINUS = '−';

/** @param {number} n */
function groupThousands(n) {
  const s = String(Math.trunc(Math.abs(n)));
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return out;
}

/**
 * "₱1,212" for whole pesos, "₱348.16" otherwise, "−₱1,240" for negatives.
 * @param {number | null | undefined} centavos
 * @returns {string}
 */
export function peso(centavos) {
  if (centavos === null || centavos === undefined || !Number.isFinite(centavos)) return '—';
  const c = Math.round(centavos);
  const neg = c < 0;
  const abs = Math.abs(c);
  const whole = Math.floor(abs / 100);
  const cents = abs % 100;
  const body = groupThousands(whole) + (cents ? '.' + String(cents).padStart(2, '0') : '');
  return (neg ? MINUS : '') + '₱' + body;
}

/**
 * Pesos as a plain input string ("49" or "49.5"), for editing centavo settings.
 * @param {number} centavos
 */
export function centavosToPesoInput(centavos) {
  if (!Number.isFinite(centavos)) return '';
  const whole = Math.trunc(centavos / 100);
  const cents = Math.abs(centavos % 100);
  if (!cents) return String(whole);
  return `${whole}.${String(cents).padStart(2, '0')}`.replace(/0$/, '');
}

/** @param {number | null | undefined} n */
export function count(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return (n < 0 ? MINUS : '') + groupThousands(n);
}

/** @param {number | null | undefined} pct */
export function percent(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
  return `${Math.round(pct)}%`;
}

/** @param {number | null | undefined} m */
export function minutes(m) {
  if (m === null || m === undefined || !Number.isFinite(m)) return '—';
  return `${Math.round(m)} min`;
}

/**
 * "24:10" style countdown from milliseconds (never negative).
 * @param {number} ms
 */
export function countdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** "MON 5 OCT" in local time. @param {Date} d */
export function dayStamp(d) {
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "11:31" in local time. @param {string | Date} iso */
export function clock(iso) {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "UPDATED 2 S AGO" / "UPDATED 3 MIN AGO". @param {number} updatedAt @param {number} now */
export function updatedAgo(updatedAt, now) {
  if (!updatedAt) return 'LOADING';
  const s = Math.max(0, Math.round((now - updatedAt) / 1000));
  if (s < 60) return `UPDATED ${s} S AGO`;
  return `UPDATED ${Math.floor(s / 60)} MIN AGO`;
}

/** Two-letter initials for the avatar. @param {string | null | undefined} name @param {string | null | undefined} email */
export function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  const a = parts[0]?.[0] || '?';
  const b = parts.length > 1 ? parts[1]?.[0] || '' : '';
  return (a + b).toUpperCase();
}

/** @param {string} method */
export function paymentLabel(method) {
  return { cod: 'COD', gcash: 'GCASH', maya: 'MAYA', card: 'CARD' }[method] || method.toUpperCase();
}

/** Statements for a first-party shop (PopCart doesn't charge itself): said in words, never "0 %". */
export const FIRST_PARTY_LABEL = 'First-party · no commission';

/** True only when the server says so (old servers don't send the flag). @param {{ firstParty?: boolean } | null | undefined} s */
export function isFirstParty(s) {
  return s?.firstParty === true;
}
